import Vue from "vue";
import Router from "vue-router";
import Home from "./views/dashboard/Home.vue";
import Archive from "./views/cases/Archive.vue";
import Listing from "./views/cases/Listing.vue";
import Find from "./views/search/QuickFind.vue";
import Forgot from "./components/user/forgot";
import Reset from "./components/user/reset";
import Activate from "./components/user/activate";
import NotFound from "./components/NotFound";
import { beforeRouteEnter } from "./router/navigationGuard";
import AdvancedSearch from "./views/search/AdvancedSearch.vue";
import reportsV1 from "./components/reports/reportsV1.vue";
import store from "@/store";

Vue.use(Router);

export const router = new Router({
  mode: "history",
  routes: [
    // Home
    {
      path: "/",
      meta: {
        title: "Welcome",
      },
      component: Home,
      title: Home,
    },
    {
      path: "/home",
      name: "home",
      component: () => import("./views/dashboard/Home.vue"),
      meta: {
        title: "Home",
        keepAlive: true,
        cacheTimeout: 300000, // 5 minutes
      },
      beforeEnter: beforeRouteEnter,
    },
    {
      path: "/archive",
      meta: {
        title: "Archive",
      },
      component: Archive,
      title: Archive,
    },
    // Listing
    {
      path: "/listing",
      meta: {
        title: "Listing",
      },
      component: Listing,
      title: Listing,
    },

    // Quickfind
    {
      path: "/find",
      name: "find",
      meta: {
        title: "Quick Find",
      },
      component: Find,
    },
    // Login
    {
      path: "/login",
      name: "login",
      component: () => import("./views/auth/Login.vue"),
      beforeEnter: async (to, from, next) => {
        try {
          const BulletinDataService = (await import("@/services/BulletinDataService")).default;
          const response = await BulletinDataService.getAll();
          // Pass bulletins via route meta so component can access immediately
          to.meta.bulletins = response.data.slice(-2).reverse();
        } catch (e) {
          console.warn("Failed to pre-fetch bulletins:", e);
          to.meta.bulletins = [];
        }
        next();
      },
    },
    // Forgot
    {
      path: "/forgot",
      name: "forgot",
      component: Forgot,
    },
    // Reset
    {
      path: "/reset",
      name: "reset",
      component: Reset,
    },
    // Activate
    {
      path: "/activate",
      name: "activate",
      component: Activate,
    },
    // Profile
    {
      path: "/profile",
      meta: {
        title: "Profile",
      },
      name: "profile",
      component: () => import("./views/profile/Profile.vue"),
    },
    // Admin
    {
      path: "/admin",
      meta: {
        title: "Admin",
      },
      name: "admin",
      component: () => import("./views/admin/AdminBoard.vue"),
    },
    // Manager
    {
      path: "/manager",
      meta: {
        title: "Manager",
      },
      name: "manager",
      component: () => import("./views/admin/ManagerBoard.vue"),
    },
    // User
    {
      path: "/user",
      meta: {
        title: "User",
      },
      name: "user",
      component: () => import("./views/admin/UserBoard.vue"),
    },
    // MEMBERS ROUTES
    {
      path: "/members",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "Members",
      },
      name: "members",
      component: () => import("./components/members/MemberList"),
    },
    // BRANCHES ROUTES
    {
      path: "/branches",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "Branches",
      },
      name: "branches",
      component: () => import("./components/branches/BranchList"),
    },
    // STATES ROUTES
    {
      path: "/states",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "States",
      },
      name: "states",
      component: () => import("./components/states/StateList"),
    },
    // REGION ROUTES
    {
      path: "/districts",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "Districts",
      },
      name: "districts",
      component: () => import("./components/regions/RegionList"),
    },

    // TATCHART ROUTES
    {
      path: "/tatcharts",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "TAT Charts",
      },
      name: "tatcharts",
      component: () => import("./components/tatcharts/TatChartList"),
    },

    // HANDLER ROUTES
    {
      path: "/handlers",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "Claim Handler",
      },
      name: "handler",
      component: () => import("./components/handler/handler"),
    },
    // BULLETIN ROUTES
    {
      path: "/bulletins",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "Bulletin",
      },
      name: "bulletins",
      component: () => import("./components/bulletins/bulletinList"),
    },
    // DEPARTMENTS ROUTES
    {
      path: "/depts",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "Departments",
      },
      name: "depts",
      component: () => import("./components/depts/DeptList"),
    },

    // FILE CLASSIFICATION ROUTES ROUTES
    {
      path: "/subDepts",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "File Classification",
      },
      name: "subDepartments",
      component: () => import("./components/subDepts/subDeptList"),
    },

    // INSURER ROUTES
    {
      path: "/inss",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN"], // This restricts access to ADMIN users only
        title: "Insurer",
      },
      name: "inss",
      component: () => import("./components/inss/InsList"),
    },

    // CLAIMS
    {
      path: "/claims",
      meta: {
        title: "Claims",
      },
      name: "claimsList",
      component: () => import("./components/claims/claimsList"),
    },

    // CLAIMS V2
    {
      path: "/claimsV2",
      meta: {
        title: "Claims Dashboard",
      },
      name: "claimsV2",
      component: () => import("./components/claimsV2/claimListV2"),
    },

    // DROPBOX FILE ROUTES
    {
      path: "/dropboxfiles",
      meta: {
        title: "Dropbox Files",
      },
      name: "dropboxfiles",
      component: () => import("./components/dropboxfiles/DropboxfileList"),
    },
    // REPORTS ROUTES
    // {
    //   path: "/reports",
    //   meta: {
    //     title: "Basic Reports",
    //   },
    //   name: "reports",
    //   component: () => import("./components/reports/reports"),
    // },
    // {
    //   path: "/reportsTwo",
    //   meta: {
    //     title: "Detail Reports",
    //   },
    //   name: "reportsTwo",
    //   component: () => import("./components/reports/reportsTwo"),
    // },
    {
      path: "/reportsV1",
      meta: {
        title: "Search Reports",
      },
      name: "reportsV1",
      component: () => import("./components/reports/reportsV1"),
    },
    {
      path: "/reports-master",
      meta: {
        title: "Reports Master",
      },
      name: "reportsMaster",
      component: () => import("./components/reports/reports-master.vue"),
    },
    {
      path: "/byref/:id",
      meta: {
        title: "Detail By Department",
      },
      name: "refTabDetail",
      component: () => import("./views/detail/ReferenceDetail.vue"),
    },
    {
      path: "/bybranch/:id",
      meta: {
        title: "Detail by Branch",
      },
      name: "brTabDetail",
      component: () => import("./views/detail/BranchDetail.vue"),
    },
    {
      path: "/detailByRef/:fileStatus/:refType",
      meta: {
        title: "Detail By Reference",
      },
      name: "detail-by-ref",
      component: () => import("./views/detail/ReferenceDetailByStatus.vue"),
    },
    {
      path: "/detailByBranch/:fileStatus/:branch",
      meta: {
        title: "Detail By Branch",
      },
      name: "detail-by-branch",
      component: () => import("./views/detail/BranchDetailByStatus.vue"),
    },
    // File
    {
      path: "/file/:id",
      meta: {
        title: "Casefile Details",
      },
      name: "vuexFile",
      component: () => import("./views/detail/CaseDetail.vue"),
    },
    // Fast detail route - always registered. Component will redirect if feature disabled.
    {
      path: "/file-fast/:id",
      meta: {
        title: "Casefile Details (Fast)",
      },
      name: "fileFast",
      component: () => import("./views/detail/CaseDetailFast.vue"),
    },
    {
      path: "/dev/debug-status",
      meta: { title: "Dev Debug Status" },
      name: "dev-debug",
      component: () => import("./views/debug/DevDebug.vue"),
    },
    {
      path: "/dopFile/:id",
      meta: {
        title: "Dropbox File Details",
      },
      name: "dropboxDetail",
      component: () => import("./views/detail/DropboxDetail.vue"),
    },

    {
      path: "/findfile/:id",
      meta: {
        title: "Casefile Details",
      },
      name: "findfile",
      component: () => import("./views/detail/QuickFindDetail.vue"),
    },

    // MISC ROUTES
    {
      path: "/soon",
      meta: {
        title: "Soon",
      },
      name: "soon",
      component: () => import("./components/Soon"),
    },

    // Merimen Routes
    {
      path: "/merimen-data",
      name: "merimen-data",
      meta: {
        requiresAuth: true,
        roles: ["ADMIN", "MANAGER", "BRANCHMANAGER", "CLERK", "BRANCHCLERK"],
        title: "Merimen Data",
      },
      component: () => import("./components/merimen/MerimenDataList.vue"),
    },

    // {
    //   path: "/dashboard",
    //   name: "dashboard",
    //   component: () => import("./views/Dashboard.vue"),
    //   meta: { requiresAuth: true, title: "dashboard", keepAlive: true },
    // },
    {
      path: "/welcome",
      name: "welcome",
      component: () => import("./views/dashboard/Welcome.vue"),
      meta: { title: "welcome" },
    },

    {
      path: "/claimsv3",
      name: "claimsv3",
      component: () => import("./views/claims/MonthlyClaimsDashboard.vue"),
      meta: { requiresAuth: true, title: "Claims Dashboard" },
    },

    {
      path: "/claims-report",
      name: "claims-report",
      component: () => import("./views/claims/ClaimsReport.vue"),
      meta: { requiresAuth: true, title: "Claims Report" },
    },

    {
      path: "/claimsv3/bookkeeping",
      name: "claimsv3-bookkeeping",
      component: () => import("./views/claims/ClaimsBookkeeping.vue"),
      meta: { requiresAuth: true, title: "Claims Bookkeeping" },
    },

    {
      path: "/advanced-search",
      name: "advancedSearch",
      component: AdvancedSearch,
      meta: {
        requiresAuth: true,
        title: "Advanced Search",
      },
    },

    {
      path: "/report",
      name: "report",
      component: () => import("./views/reports/Report.vue"),
      meta: {
        requiresAuth: true,
        // roles: ["ADMIN", "MANAGER", "BRANCHMANAGER", "CLERK", "BRANCHCLERK"],
        title: "Advanced Search",
      },
    },

    {
      path: "/mytasks",
      name: "mytasks",
      component: () => import("./views/cases/MyTasks.vue"),
      meta: {
        requiresAuth: true,
        title: "My Tasks",
      },
    },

    {
      path: "/reports-v1",
      name: "ReportsV1",
      component: reportsV1,
    },
    // reportsTwo removed
    {
      path: "/compliance",
      component: () => import("./components/compliance/complianceBase.vue"),
      meta: {
        requiresAuth: true,
        roles: ["ADMIN", "MANAGER", "CLERK"], // restrict to these roles
        title: "Compliance",
      },
      children: [
        // Redirect /compliance to /compliance/closed-files for users with permission
        { path: "", redirect: "closed-files" },
        {
          path: "closed-files",
          name: "compliance-closed-files",
          meta: { title: "Closed Files by Department" },
          component: () =>
            import("./components/compliance/ClosedFilesReport.vue"),
        },
        {
          path: "compliance-ratio-dept-insurer",
          name: "compliance-ratio-dept-insurer",
          meta: { title: "Compliance Ratio (By Insurer)" },
          component: () =>
            import("./components/compliance/ComplianceRatioInsurerDept.vue"),
        },
        // {
        //   path: "compliance-ratio-insurer",
        //   name: "compliance-ratio-insurer",
        //   meta: { title: "Compliance Ratio (By Insurer)" },
        //   component: () =>
        //     import("./components/compliance/ComplianceRatioInsurer.vue"),
        // },
        {
          path: "compliance-ratio-branch",
          name: "compliance-ratio-branch",
          meta: { title: "Compliance Ratio (By Branch)" },
          component: () =>
            import("./components/compliance/ComplianceRatioBranch.vue"),
        },
        {
          path: "outstanding-insurer",
          name: "compliance-outstanding-insurer",
          meta: { title: "Outstanding Assignment (By Insurer)" },
          component: () =>
            import("./components/compliance/OutstandingInsurer.vue"),
        },
        {
          path: "outstanding-branch",
          name: "compliance-outstanding-branch",
          meta: { title: "Outstanding Assignment (By Branch)" },
          component: () =>
            import("./components/compliance/OutstandingBranch.vue"),
        },
        {
          path: "outstanding-days-insurer",
          name: "compliance-outstanding-days-insurer",
          meta: { title: "Outstanding Assignment by Days (Insurer)" },
          component: () =>
            import("./components/compliance/OutstandingDaysInsurer.vue"),
        },
        {
          path: "outstanding-days-branch",
          name: "compliance-outstanding-days-branch",
          meta: { title: "Outstanding Assignment by Days (Branch)" },
          component: () =>
            import("./components/compliance/OutstandingDaysBranch.vue"),
        },
        {
          path: "ratio-calculator-insurer",
          name: "compliance-ratio-calculator-insurer",
          meta: { title: "Ratio Calculator by Closed Files (Insurer)" },
          component: () =>
            import("./components/compliance/RatioCalculatorInsurer.vue"),
        },
        {
          path: "compliance-table",
          name: "compliance-table",
          meta: { title: "Compliance Table" },
          component: () =>
            import("./components/compliance/ComplianceTable.vue"),
        },
        {
          path: "outstanding-master",
          name: "compliance-outstanding-master",
          meta: { title: "Outstanding Master List" },
          component: () =>
            import("./components/compliance/OutstandingMaster.vue"),
        },
        {
          path: "closed-by-adjuster-date",
          name: "compliance-closed-by-adjuster-date",
          meta: { title: "Closed by Adjuster (By Date)" },
          component: () =>
            import("./components/compliance/ClosedByAdjusterDate.vue"),
        },
        {
          path: "closed-by-adjuster-month",
          name: "compliance-closed-by-adjuster-month",
          meta: { title: "Closed by Adjuster (By Month)" },
          component: () =>
            import("./components/compliance/ClosedByAdjusterMonth.vue"),
        },
        {
          path: "closed-by-clerk-date",
          name: "compliance-closed-by-clerk-date",
          meta: { title: "Closed by Clerk (By Date)" },
          component: () =>
            import("./components/compliance/ClosedByClerkDate.vue"),
        },
        {
          path: "closed-by-clerk-month",
          name: "compliance-closed-by-clerk-month",
          meta: { title: "Closed by Clerk (By Month)" },
          component: () =>
            import("./components/compliance/ClosedByClerkMonth.vue"),
        },
        // --- Add new modules below ---
        {
          path: "outstanding-by-adjuster-date",
          name: "compliance-outstanding-by-adjuster-date",
          meta: { title: "Outstanding Assignment By Adjuster (By Date)" },
          component: () =>
            import("./components/compliance/OutstandingByAdjusterDate.vue"),
        },
        {
          path: "received-days-insurer",
          name: "compliance-received-days-insurer",
          meta: { title: "Received Assignment by Days (Insurer)" },
          component: () =>
            import("./components/compliance/ReceivedDaysInsurer.vue"),
        },
        {
          path: "received-month-insurer",
          name: "compliance-received-month-insurer",
          meta: { title: "Received Assignment by Month (Insurer)" },
          component: () =>
            import("./components/compliance/ReceivedMonthInsurer.vue"),
        },
      ],
    },

    // {
    //   path: "/logs",
    //   meta: {
    //     requiresAuth: true,
    //     roles: ["ADMIN", "MANAGER"],
    //     title: "Log Dashboard",
    //   },
    //   name: "logDashboard",
    //   component: () => import("./components/management/logDashboard.vue"),
    // },

    {
      path: "/management",
      meta: {
        requiresAuth: true,
        roles: ["SUPER_ADMIN"], // super-admin only
        title: "App Management",
      },
      name: "management",
      component: () => import("./components/management/management.vue"),
    },

    {
      path: "/notfound",
      name: "NotFound",
      component: NotFound,
    },
    {
      path: "/:catchAll(.*)",
      component: NotFound,
    },
  ],
});
router.beforeEach(async (to, from, next) => {
  const publicPages = ["/login", "/forgot", "/reset", "/activate"];
  const authRequired = !publicPages.includes(to.path);

  // Centralized session check
  if (authRequired) {
    await store.dispatch("auth/ensureValidSession");
  }

  const user = store.getters["auth/currentUserSafe"];
  const loggedIn = user && user.id !== null;

  if (authRequired && !loggedIn) {
    next("/login");
    return;
  }

  // Update LastSeen for authenticated users on every route change
  if (loggedIn && user.id) {
    // Fire-and-forget, don't block navigation
    import("./services/auth.service").then((mod) => {
      mod.default.updateLastSeen(user.id).catch(() => {});
    });
  }

  // Check for role-based permissions (including nested/child routes)
  const matchedWithRoles = to.matched.find(
    (record) => record.meta && record.meta.roles,
  );
  if (matchedWithRoles && loggedIn) {
    const hasRequiredRole =
      user &&
      user.roles &&
      matchedWithRoles.meta.roles.some((role) => user.roles.includes(role));

    if (!hasRequiredRole) {
      next("/notfound");
      return;
    }
  }

  next();
});

// router.afterEach((to, from) => {
//   // REMOVE or COMMENT OUT this block to prevent page_view logging
//   logEvent({
//     action: "page_view",
//     feature: to.name || to.path,
//     status: "success",
//     user: store.state.auth?.user,
//     eventTitle: `Visited ${to.name || to.path}`,
//     meta: { from: from.fullPath, to: to.fullPath },
//   });
// });
